import { cva } from "class-variance-authority";
import React, { forwardRef, useRef, useLayoutEffect, useState } from "react";
import { cn } from "src/lib/cn";
// WithReset is authored in JS; type it loosely for ref and props passthrough
import { WithReset } from "src/sudokit/Layout/BoxReset";
const ResetAny = WithReset as unknown as React.ComponentType<any>;

type SegmentedControlVariant = "secondary" | "tertiary";
type SegmentedControlSize = "default" | "small" | "large";

export type SegmentedControlOption = {
  value: string;
  label: string;
  disabled?: boolean;
  leftIcon?: string;
  rightIcon?: string;
};

export type SudoSegmentedControlProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> & {
  options: SegmentedControlOption[];
  value: string;
  onChange?: (value: string) => void;
  variant?: SegmentedControlVariant;
  size?: SegmentedControlSize;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  equalWidth?: boolean;
};

const segmentedControlVariants = cva(
  "inline-flex items-center justify-center gap-0 rounded-xl border border-solid border-app-grey-3 bg-app-grey-4 p-1",
  {
    variants: {
      variant: {
        secondary: "bg-app-grey-4 border-app-grey-3",
        tertiary: "bg-transparent border-transparent",
      },
      size: {
        default: "h-10",
        small: "h-8",
        large: "h-12",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "default",
    },
  }
);

const segmentedControlItemVariants = cva(
  "relative inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg font-medium cursor-pointer whitespace-nowrap select-none app-region-no-drag border-none bg-transparent transition-all duration-200 ease-in-out disabled:pointer-events-none disabled:opacity-50 font-inter",
  {
    variants: {
      variant: {
        secondary: "",
        tertiary: "",
      },
      size: {
        default: "text-sm min-h-8",
        small: "text-xs min-h-6 px-2 py-1",
        large: "text-base min-h-10 px-4 py-2",
      },
      state: {
        active: "",
        inactive: "",
      },
    },
    compoundVariants: [
      {
        variant: "secondary",
        state: "active",
        class: "bg-app-document-background text-app-grey-0 shadow-sm",
      },
      {
        variant: "secondary",
        state: "inactive",
        class: "text-app-grey-1 hover:text-app-grey-0 hover:bg-app-document-background/50",
      },
      {
        variant: "tertiary",
        state: "active",
        class: "bg-app-primary-cta-translucent text-app-primary-cta-high-contrast",
      },
      {
        variant: "tertiary",
        state: "inactive",
        class: "text-app-grey-1 hover:text-app-grey-0 hover:bg-app-grey-4",
      },
    ],
    defaultVariants: {
      variant: "secondary",
      size: "default",
      state: "inactive",
    },
  }
);

export const SudoSegmentedControl = forwardRef<HTMLDivElement, SudoSegmentedControlProps>(
  ({
    options,
    value,
    onChange,
    variant = "secondary",
    size = "default",
    disabled = false,
    className,
    "aria-label": ariaLabel,
    equalWidth = false,
    ...props
  }, ref) => {
    const handleChange = (optionValue: string) => {
      if (!disabled && onChange) {
        onChange(optionValue);
      }
    };

    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>,
      optionValue: string,
    ) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleChange(optionValue);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const currentIndex = options.findIndex(option => option.value === value);
        const nextIndex = event.key === 'ArrowLeft'
          ? (currentIndex - 1 + options.length) % options.length
          : (currentIndex + 1) % options.length;

        const nextOption = options[nextIndex];
        if (nextOption && !nextOption.disabled) {
          handleChange(nextOption.value);
          buttonRefs.current[nextIndex]?.focus();
        }
      }
    };

    const measureRef = useRef<HTMLDivElement | null>(null);
    const [maxWidth, setMaxWidth] = useState<number | null>(null);
    // Parent controls often create option arrays inline. Only width-affecting changes
    // should retrigger the synchronous measurement and state update below.
    const optionLabels = JSON.stringify(options.map(option => option.label));
    const hasOptionIcons = options.some(option => option.leftIcon || option.rightIcon);

    useLayoutEffect(() => {
      if (!equalWidth || !measureRef.current) return;

      const measureElement = document.createElement('div');
      measureElement.style.position = 'absolute';
      measureElement.style.visibility = 'hidden';
      measureElement.style.whiteSpace = 'nowrap';
      const button = measureRef.current.querySelector('button');
      if (button) {
        const computed = getComputedStyle(button);
        measureElement.style.fontSize = computed.fontSize;
        measureElement.style.fontFamily = computed.fontFamily as string;
        measureElement.style.fontWeight = computed.fontWeight as string;
      }
      document.body.appendChild(measureElement);

      let maxTextWidth = 0;
      (JSON.parse(optionLabels) as string[]).forEach(label => {
        measureElement.textContent = label;
        const textWidth = measureElement.offsetWidth;
        maxTextWidth = Math.max(maxTextWidth, textWidth);
      });

      const iconSpace = hasOptionIcons ? (size === "small" ? 16 : size === "large" ? 24 : 20) : 0;
      const gapSpace = hasOptionIcons ? 8 : 0;
      const paddingSpace = size === "small" ? 16 : size === "large" ? 32 : 24;

      const calculatedWidth = maxTextWidth + iconSpace + gapSpace + paddingSpace;
      setMaxWidth(currentWidth => currentWidth === calculatedWidth ? currentWidth : calculatedWidth);

      document.body.removeChild(measureElement);

      return () => {
        if (document.body.contains(measureElement)) {
          document.body.removeChild(measureElement);
        }
      };
    }, [optionLabels, hasOptionIcons, equalWidth, size]);

    return (
      <ResetAny
        ref={equalWidth ? (measureRef as unknown as React.Ref<HTMLDivElement>) : ref}
        role="tablist"
        aria-label={ariaLabel}
        className={cn(segmentedControlVariants({ variant, size }), className)}
        {...props}
      >
        {options.map((option, index) => {
          const isActive = option.value === value;
          const isDisabled = disabled || option.disabled;

          return (
            <ResetAny
              key={option.value}
              ref={(el: HTMLButtonElement | null) => { buttonRefs.current[index] = el; }}
              as="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${option.value}`}
              tabIndex={isActive ? 0 : -1}
              disabled={isDisabled}
              onClick={() => handleChange(option.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => handleKeyDown(e, option.value)}
              className={cn(
                segmentedControlItemVariants({
                  variant,
                  size,
                  state: isActive ? "active" : "inactive"
                }),
                isDisabled && "cursor-not-allowed opacity-50"
              )}
              style={equalWidth && maxWidth ? { width: `${maxWidth}px` } : undefined}
            >
              {option.leftIcon && (
                <img
                  src={option.leftIcon}
                  alt=""
                  className={cn(
                    "flex-shrink-0",
                    size === "small" ? "w-3 h-3" : size === "large" ? "w-5 h-5" : "w-4 h-4"
                  )}
                  aria-hidden="true"
                />
              )}

              <span>{option.label}</span>

              {option.rightIcon && (
                <img
                  src={option.rightIcon}
                  alt=""
                  className={cn(
                    "flex-shrink-0",
                    size === "small" ? "w-3 h-3" : size === "large" ? "w-5 h-5" : "w-4 h-4"
                  )}
                  aria-hidden="true"
                />
              )}
            </ResetAny>
          );
        })}
      </ResetAny>
    );
  }
);

SudoSegmentedControl.displayName = "SudoSegmentedControl";
